import React, { useMemo, useCallback, useState, useEffect } from 'react'
import { createEditor, Editor, Transforms } from 'slate'
import { Slate, Editable, withReact } from 'slate-react'
import { withHistory } from 'slate-history'
import { htmlToSlate, slateToHtml } from '@slate-serializers/html'

import {
  renderNode,
  serializeConfig,
  deserializeConfig,
  CustomEditor,
  withInlines,
} from '@/components/richTextEditor/helpers'

import Toolbar from './Toolbar'

const RichTextEditor = ({ field, form, disabled, inlineVerbatim }) => {
  const editor = useMemo(() => withInlines(withHistory(withReact(createEditor()))), [])

  const [value, setValue] = useState(() => {
    const content = field.value || '<p></p>'
    return htmlToSlate(content, deserializeConfig)
  })

  useEffect(() => {
    const newHtml = slateToHtml(value, serializeConfig)
    // forces the display preview to update with class custom css,
    // happens only on load as onChange handles other cases
    setTimeout(() => {
      form.setFieldValue('display.html', newHtml)
    }, 0)
  }, [])

  const renderElement = useCallback((props) => {
    return renderNode(props)
  }, [])

  const renderLeaf = useCallback((props) => {
    return <Leaf {...props} />
  }, [])

  const handleDOMBeforeInput = useCallback(
    (event) => {
      switch (event.inputType) {
        case 'formatBold':
          event.preventDefault()
          CustomEditor.toggleBoldMark(editor)
          break
        case 'formatItalic':
          event.preventDefault()
          CustomEditor.toggleItalicMark(editor)
          break
        default:
          break
      }
    },
    [editor]
  )

  const handleDragStart = useCallback((event) => {
    const element = event.target.closest('[data-slate-node="element"]')
    if (!element || element.getAttribute('data-slate-type') !== 'image') return

    event.dataTransfer.setData('text/plain', element.querySelector('img').src)
    event.dataTransfer.effectAllowed = 'move'
  }, [])

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault()
      const url = event.dataTransfer.getData('text/plain')
      if (!url) return

      const { selection } = editor
      if (!selection) return

      const point = Editor.after(editor, selection)
      Transforms.insertNodes(
        editor,
        {
          type: 'image',
          url,
          children: [{ text: '' }],
        },
        { at: point }
      )
    },
    [editor]
  )

  const handleDragOver = useCallback((event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  return (
    <div className="rich-text-editor-container">
      <Slate
        editor={editor}
        value={value}
        onChange={(newValue) => {
          setValue(newValue)
          const html = slateToHtml(newValue, serializeConfig)
          form.setFieldValue('display.html', html)
        }}
      >
        <Toolbar editor={editor} CustomEditor={CustomEditor} inlineVerbatim={inlineVerbatim} />
        <Editable
          style={{ padding: '1rem' }}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          spellCheck
          readOnly={disabled}
          onDOMBeforeInput={handleDOMBeforeInput}
          onDragStart={handleDragStart}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onKeyDown={(event) => {
            if (!event.ctrlKey) return

            switch (event.key) {
              case 'b': {
                event.preventDefault()
                CustomEditor.toggleBoldMark(editor)
                break
              }
              case 'i': {
                event.preventDefault()
                CustomEditor.toggleItalicMark(editor)
                break
              }
              default:
                break
            }
          }}
        />
      </Slate>
    </div>
  )
}

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>
  }
  if (leaf.italic) {
    children = <em>{children}</em>
  }
  return <span {...attributes}>{children}</span>
}

export default RichTextEditor
